import {
    faExclamationCircle,
    faExclamationTriangle,
    faFire,
    IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import {
    type AlertResponse,
    ApplicationRoleCode,
    IIssueActivityUserChild,
    IIssueChild,
    IssueHardwareHealthResponse,
    IssueHardwareType,
    IssueMachineHealthResponse,
    IssueRemoveReason,
    IssueSavingsStatus,
    IssueStatusType,
    IUserResponse,
} from '@kcftech/prometheus-client';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { subDays, subHours, subMonths, subWeeks } from 'date-fns';
import { IssueUserInfo } from 'pages/Desk/issues/types';
import { SeverityFilter, TimeRangeFilter } from 'shared/issue_filters_context/types';
import { type Normalized, type NormalizedAlertIssueChild } from 'shared/normalized_cache';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { sortBy, toMap, uniqueBy } from 'utils/array';
import { isNodeInactive } from 'utils/node';
import { Filter, FilterExpr } from 'utils/query_v3';
import { RecurseNonSdkClass } from 'utils/sdk_types';

export const IssueDescriptionMaxLength = 5000;
export const IssueRecommendedActionMaxLength = 1000;
export const IssueCommentMaxLength = 10000;
export const IssueArchiveCommentMaxLength = 1000;

export const SevereIssueSeverityThreshold = 0.6;
export const LowIssueSeverityThreshold = 0.3;

export const IssuesDefaultSort: DataSourceSort[] = [
    { field: 'Severity', dir: 'desc' },
    { field: 'Priority', dir: 'desc' },
    { field: 'Created', dir: 'asc' },
];

export type IssueFilter = Readonly<{
    id?: string;
    locationId?: string;
    issueNumber?: number;
    description?: string;
    statusName?: string;
    statusType?: IssueStatusType[];
    priority?: number[];
    severity?: SeverityFilter[];
    faultTypeIds?: string[];
    savings?: IssueSavingsStatus[];
    userId?: string;
    userEmail?: string;
    createdBy?: string;
    created?: Date | TimeRangeFilter;
    lastActivity?: Date;
    nameSearch?: string;
    hierarchyGroupIds?: string[];
    dynamicSystemIds?: string[];
    hasHealthRanges?: boolean;
    hardwareTypes?: IssueHardwareType[];
    isInternal?: boolean;
    hardwareId?: number;
    isFlagged?: boolean;
    shouldReceiveNotifications?: boolean;
    manuallyCreated?: boolean;
    isResolved?: boolean;

    // Optional filter suffix expression (to allow using mixed and/or)
    suffixExpression?: string;
}>;

const getDurationFilter = (timeRangeFilter: Date | TimeRangeFilter): Date => {
    if (timeRangeFilter instanceof Date) {
        return timeRangeFilter;
    }
    switch (timeRangeFilter) {
        case 'past-12-hours':
            return subHours(new Date(), 12);

        case 'past-day':
            return subDays(new Date(), 1);

        case 'past-week':
            return subWeeks(new Date(), 1);

        case 'past-month':
            return subMonths(new Date(), 1);

        default:
            throw new Error(`Unsupported time range filter: ${timeRangeFilter}`);
    }
};

const getResolvedFilter = (isResolved: boolean): FilterExpr => {
    if (isResolved) {
        return Filter.isNotNull('DateResolved');
    }
    return Filter.isNull('DateResolved');
};

export const buildFilterExpression = (
    mode: IssueMode,
    {
        id,
        locationId,
        issueNumber,
        description,
        statusName,
        statusType,
        priority,
        severity,
        faultTypeIds,
        savings,
        userId,
        userEmail,
        createdBy,
        created,
        lastActivity,
        hasHealthRanges,
        hardwareTypes,
        dynamicSystemIds,
        isInternal,
        hardwareId,
        isFlagged,
        shouldReceiveNotifications,
        manuallyCreated,
        isResolved,
        nameSearch,
        suffixExpression,
    }: Partial<IssueFilter>,
    filterAggregate?: 'and' | 'or',
) => {
    const aggregateFn =
        filterAggregate === undefined || filterAggregate === 'and'
            ? Filter.and
            : Filter.or;

    return aggregateFn(
        id ? Filter.eq('Id', id) : undefined,
        locationId ? Filter.eq('LocationId', locationId) : undefined,
        issueNumber ? Filter.eq('IssueNumber', issueNumber) : undefined,
        description ? Filter.eq('Description', description) : undefined,
        priority ? Filter.in('Priority', priority) : undefined,
        statusName ? Filter.eq('StatusName', statusName) : undefined,
        statusType ? Filter.in('StatusType', statusType) : undefined,
        severity
            ? Filter.or(
                  ...severity.map((value) => {
                      switch (value) {
                          // severe severity: > 0.6
                          case 'severe':
                              return Filter.gt('Severity', SevereIssueSeverityThreshold);
                          // low severity: < 0.3
                          case 'low':
                              return Filter.lt('Severity', LowIssueSeverityThreshold);
                          // moderate severity: >= 0.3 and =< 0.6
                          default:
                              return Filter.and(
                                  Filter.geq('Severity', LowIssueSeverityThreshold),
                                  Filter.leq('Severity', SevereIssueSeverityThreshold),
                              );
                      }
                  }),
              )
            : undefined,
        faultTypeIds ? Filter.anyIn('FaultTypes.Id', faultTypeIds) : undefined,
        savings ? Filter.in('IssueSavingsStatus', savings) : undefined,
        userId ? Filter.eq('UserId', userId) : undefined,
        userEmail ? Filter.eq('UserEmail', userEmail) : undefined,
        createdBy ? Filter.eq('CreatedBy', createdBy) : undefined,
        created ? Filter.geq('Created', getDurationFilter(created)) : undefined,
        lastActivity ? Filter.geq('LastActivity', lastActivity) : undefined,
        hasHealthRanges !== undefined
            ? Filter.eq('HasHealthRanges', hasHealthRanges)
            : undefined,
        hardwareTypes ? Filter.in('IssueHardwareType', hardwareTypes) : undefined,
        dynamicSystemIds ? Filter.in('DynamicSystem.Id', dynamicSystemIds) : undefined,
        isInternal !== undefined ? Filter.eq('IsInternal', isInternal) : undefined,
        isFlagged !== undefined ? Filter.eq('IsFlagged', isFlagged) : undefined,
        shouldReceiveNotifications !== undefined
            ? Filter.eq('ShouldReceiveNotifications', shouldReceiveNotifications)
            : undefined,
        manuallyCreated !== undefined
            ? Filter.eq('ManuallyCreated', manuallyCreated)
            : undefined,
        isResolved !== undefined ? getResolvedFilter(isResolved) : undefined,
        hardwareId !== undefined
            ? Filter.or(
                  Filter.eq('BaseStation.BaseStationId', hardwareId),
                  Filter.eq('Repeater.ReceiverId', hardwareId),
                  Filter.eq('Node.NodeId', hardwareId),
                  Filter.eq('Hub.HubId', hardwareId),
              )
            : undefined,
        nameSearch
            ? Filter.or(
                  Filter.in('Priority', findPriorities(nameSearch)),
                  Filter.in('Severity', findSeverities(nameSearch)),
                  mode === 'hardwareHealth'
                      ? Filter.search(
                            [
                                'FormattedIssueNumber',
                                'HardwareSerialNumber',
                                'StatusName',
                            ],
                            nameSearch,
                        )
                      : undefined,
                  mode === 'machineHealth'
                      ? Filter.search(
                            [
                                'FormattedIssueNumber',
                                'HierarchyChildren.HierarchyName',
                                'StatusName',
                            ],
                            nameSearch,
                        )
                      : undefined,
              )
            : undefined,
        suffixExpression,
    );
};

export type IssueType = 'draft' | 'active' | 'archived';

export type IssueMode = 'machineHealth' | 'hardwareHealth';

export const DefaultIssueMode: IssueMode = 'machineHealth';

export const issueModeSupportsAutomaticMaintenanceTaskCreation = (issueMode: IssueMode) =>
    issueMode === 'machineHealth';

export type IssueSeverity = Readonly<{
    label: string;
    severityValue: number;
    icon: IconDefinition;
    backgroundColor: string;
    isMatch: (value: number) => boolean;
}>;

export const IssueSeverities: IssueSeverity[] = [
    {
        label: 'Severe',
        severityValue: 1,
        icon: faFire,
        backgroundColor: tokens.severity.alarm,
        isMatch: (value) => value > SevereIssueSeverityThreshold,
    },
    {
        label: 'Moderate',
        severityValue: 0.5,
        icon: faExclamationTriangle,
        backgroundColor: tokens.severity.moderate,
        isMatch: (value) =>
            value >= LowIssueSeverityThreshold && value <= SevereIssueSeverityThreshold,
    },
    {
        label: 'Low',
        severityValue: 0,
        icon: faExclamationCircle,
        isMatch: (value) => value < LowIssueSeverityThreshold,
        backgroundColor: tokens.severity.low,
    },
];

const IssueSeveritiesByLowerLabel = Object.fromEntries(
    IssueSeverities.map((sev) => [sev.label.toLowerCase(), sev]),
);

export const getIssueSeverity = (value: number): IssueSeverity => {
    const severity = IssueSeverities.find(({ isMatch: isSeverity }) => isSeverity(value));
    if (!severity) {
        throw new Error('Invalid severity');
    }
    return severity;
};

export type IssuePriority = Readonly<{
    label: string;
    priorityValue: number;
    backgroundColor: string;
    textColor: string;
}>;

export const IssuePriorities: IssuePriority[] = [
    {
        priorityValue: 1,
        label: 'Critical',
        backgroundColor: tokens.priority['1'],
        textColor: tokens.text.onColor,
    },
    {
        priorityValue: 2,
        label: 'High',
        backgroundColor: tokens.priority['2'],
        textColor: tokens.text.onColor,
    },
    {
        priorityValue: 3,
        label: 'Medium',
        backgroundColor: tokens.priority['3'],
        textColor: tokens.text.onColor,
    },
    {
        priorityValue: 4,
        label: 'Low',
        backgroundColor: tokens.priority['4'],
        textColor: tokens.background.multicolorsDefault,
    },
    {
        priorityValue: 5,
        label: 'Minimal',
        backgroundColor: tokens.priority['5'],
        textColor: tokens.background.multicolorsDefault,
    },
];

const IssuePrioritiesByLowerLabel = Object.fromEntries(
    IssuePriorities.map((sev) => [sev.label.toLowerCase(), sev]),
);

export const getIssuePriority = (value: number): IssuePriority => {
    const constrainedValue = Math.max(1, Math.min(5, Math.floor(value)));

    const priority = IssuePriorities.find(
        ({ priorityValue }) => priorityValue === constrainedValue,
    );

    if (!priority) {
        throw new Error('Invalid priority');
    }
    return priority;
};

export type IssueStatus = Readonly<{
    id: string;
    label: string;
    backgroundColor: string;
    textColor: string;
    type: IssueStatusType;
}>;

export const IssueStatuses: IssueStatus[] = [
    {
        id: 'draft',
        label: 'Draft',
        backgroundColor: tokens.status.draft,
        textColor: tokens.status.draftText,
        type: IssueStatusType.Draft,
    },
    {
        id: 'new',
        label: 'New',
        backgroundColor: tokens.status.new,
        textColor: tokens.text.onColor,
        type: IssueStatusType.New,
    },
    {
        id: 'open',
        label: 'Open',
        backgroundColor: tokens.status.open,
        textColor: tokens.text.onColor,
        type: IssueStatusType.Open,
    },
    {
        id: 'closed',
        label: 'Closed',
        backgroundColor: tokens.status.closed,
        textColor: tokens.text.onColor,
        type: IssueStatusType.Closed,
    },
    {
        id: 'wont-fix',
        label: "Won't Fix",
        backgroundColor: tokens.status.wontFix,
        textColor: tokens.text.onColor,
        type: IssueStatusType.WontFix,
    },
    {
        id: 'not-an-issue',
        label: 'Not an Issue',
        backgroundColor: tokens.status.notAnIssue,
        textColor: tokens.text.onColor,
        type: IssueStatusType.NotIssue,
    },
    {
        id: 'removed',
        label: 'Removed',
        backgroundColor: tokens.status.removed,
        textColor: tokens.text.onColor,
        type: IssueStatusType.Removed,
    },
];

const issueStatusByType = toMap(IssueStatuses, (s) => s.type);

export const getIssueStatus = (type: IssueStatusType): IssueStatus => {
    const status = issueStatusByType.get(type);

    if (!status) {
        throw new Error('Invalid status');
    }
    return status;
};

export type SavingsStatus = Readonly<{
    label: string;
    type: IssueSavingsStatus;
}>;

export const IssueSavingsStatuses: SavingsStatus[] = [
    {
        label: 'Not Entered',
        type: IssueSavingsStatus.NotEntered,
    },
    {
        label: 'Entered',
        type: IssueSavingsStatus.Entered,
    },
    {
        label: 'Not Applicable',
        type: IssueSavingsStatus.NotApplicable,
    },
    {
        label: 'Verified',
        type: IssueSavingsStatus.Approved,
    },
];

export type HardwareType = Readonly<{
    label: string;
    type: IssueHardwareType;
}>;

export const IssueHardwareTypes: HardwareType[] = [
    {
        label: 'Base Station',
        type: IssueHardwareType.BaseStation,
    },
    {
        label: 'Repeater',
        type: IssueHardwareType.Repeater,
    },
    {
        label: 'IoT HUB',
        type: IssueHardwareType.Hub,
    },
    {
        label: 'Wireless Vibration Sensor',
        type: IssueHardwareType.WirelessVibrationSensor,
    },
    {
        label: 'Wired Vibration Sensor',
        type: IssueHardwareType.WiredVibrationSensor,
    },
    { label: 'IEPE Adapter', type: IssueHardwareType.IepeAdapter },
    { label: 'Analog Adapter', type: IssueHardwareType.AnalogAdapter },
];

export const getIssueSavingsStatus = (type: IssueSavingsStatus): SavingsStatus => {
    const status = IssueSavingsStatuses.find((savings) => savings.type === type);

    if (!status) {
        throw new Error('Invalid status');
    }
    return status;
};

export type IssueRemoveReasonOption = Readonly<{
    label: string;
    type: IssueRemoveReason;
}>;

export const IssueRemoveReasons: IssueRemoveReasonOption[] = [
    {
        label: 'Equipment Is Healthy',
        type: IssueRemoveReason.EquipmentIsHealthy,
    },
    {
        label: 'Issue Outdated',
        type: IssueRemoveReason.IssueOutdated,
    },
    {
        label: 'Model Oversensitive',
        type: IssueRemoveReason.ModelOversensitive,
    },
    {
        label: 'Invalid Model Assignment',
        type: IssueRemoveReason.InvalidModelAssignment,
    },
    {
        label: 'Invalid Sensor Data',
        type: IssueRemoveReason.InvalidSensorData,
    },
    {
        label: 'Duplicate Issue',
        type: IssueRemoveReason.DuplicateIssue,
    },
    {
        label: 'Other',
        type: IssueRemoveReason.Other,
    },
];

export const getIssueRemoveReason = (
    type: IssueRemoveReason,
): IssueRemoveReasonOption => {
    const removeReason = IssueRemoveReasons.find((reason) => reason.type === type);

    if (!removeReason) {
        throw new Error('Invalid remove reason');
    }
    return removeReason;
};

/** An issue, or an issue child as embedded in other responses. */
export type IssueOrChild =
    | PlainIssue
    | RecurseNonSdkClass<IIssueChild>
    | NormalizedAlertIssueChild;

export const getIssueId = (issue: IssueOrChild) =>
    'IssueId' in issue ? issue.IssueId : issue.Id;

export const getFormattedIssueName = (issue: IssueOrChild) => {
    const issueNumber = `#${issue.IssueNumber}`;
    // issue children never show their group
    const issueGroup =
        'discriminator' in issue && isMachineHealthIssue(issue) && issue.Group
            ? ` - ${issue.Group.GroupName} -`
            : '';
    const faults =
        issue.FaultTypes.length > 0
            ? sortBy(issue.FaultTypes, ({ FullName }) => FullName)
                  .map(({ Name }) => Name)
                  .join(', ')
            : 'Issue';

    return `${issueNumber}${issueGroup} ${faults}`;
};

export const isKcfUser = (role: ApplicationRoleCode) =>
    role === ApplicationRoleCode.Sales ||
    role === ApplicationRoleCode.Sentry ||
    role === ApplicationRoleCode.GlobalAdmin;

export const isKcfAdmin = (role: ApplicationRoleCode) =>
    role === ApplicationRoleCode.Sentry || role === ApplicationRoleCode.GlobalAdmin;

export function toIssueUserInfo(
    user: IUserResponse | IIssueActivityUserChild | IssueUserInfo,
): IssueUserInfo {
    // IssueUserInfo
    if ('id' in user) {
        return user;
    }
    // IUserResponse
    if ('Id' in user) {
        return {
            id: user.Id,
            role: user.ApplicationRoleCode,
            email: user.Email,
            firstName: user.FirstName,
            lastName: user.LastName,
        };
    }
    // IIssueActivityUserChild
    if ('UserId' in user) {
        return {
            id: user.UserId,
            role: user.ApplicationRole,
            email: user.UserEmail,
            firstName: user.UserFirstName,
            lastName: user.UserLastName,
        };
    }
    throw new Error('Invalid user object');
}

export function getUniqueUserInfo(userInfo: IssueUserInfo[]): IssueUserInfo[] {
    return uniqueBy(userInfo, (user) => user.id);
}

export function sortIssueUserInfo(userInfo: IssueUserInfo[]): IssueUserInfo[] {
    return sortBy(
        userInfo,
        (user) => user.firstName,
        (user) => user.lastName,
        (user) => user.id,
    );
}

export type PlainMachineHealthIssue = RecurseNonSdkClass<IssueMachineHealthResponse>;

export type PlainHardwareHealthIssue = RecurseNonSdkClass<IssueHardwareHealthResponse>;

/**
 * An issue as plain data, discriminated on `discriminator`. SDK issue
 * instances are assignable to it, so it accepts both.
 */
export type PlainIssue = PlainMachineHealthIssue | PlainHardwareHealthIssue;

/**
 * An alert as plain data, as normalized queries hold it. Convert SDK alerts
 * with `toNormalized`.
 */
export type PlainAlert = Normalized<AlertResponse>;

/**
 * Whether `issue` is a Machine Health issue. Unlike `instanceof`, this also
 * holds for plain copies of SDK issues, e.g. ones kept in a store.
 */
export function isMachineHealthIssue<TIssue extends PlainIssue>(
    issue: TIssue,
): issue is Extract<TIssue, PlainMachineHealthIssue> {
    return issue.discriminator === 'MachineHealth';
}

/**
 * Whether `issue` is a Hardware Health issue. Unlike `instanceof`, this also
 * holds for plain copies of SDK issues, e.g. ones kept in a store.
 */
export function isHardwareHealthIssue<TIssue extends PlainIssue>(
    issue: TIssue,
): issue is Extract<TIssue, PlainHardwareHealthIssue> {
    return issue.discriminator === 'HardwareHealth';
}

export function getIssueMode(issue: PlainIssue): IssueMode {
    return isMachineHealthIssue(issue) ? 'machineHealth' : 'hardwareHealth';
}

export const getReadableIssueHardwareType = (type: IssueHardwareType) => {
    switch (type) {
        case IssueHardwareType.BaseStation:
            return 'Base Station';
        case IssueHardwareType.Repeater:
            return 'Repeater';
        case IssueHardwareType.AnalogAdapter:
            return 'Analog Adapter';
        case IssueHardwareType.Hub:
            return 'IoT HUB';
        case IssueHardwareType.IepeAdapter:
            return 'IEPE Adapter';
        case IssueHardwareType.WiredVibrationSensor:
            return 'Wired Vibration Sensor';
        case IssueHardwareType.WirelessVibrationSensor:
            return 'Wireless Vibration Sensor';
        default:
            throw new Error(
                `Unknown IssueHardwareType: ${JSON.stringify(IssueHardwareType)}`,
            );
    }
};

export const getHardwareHealthTooltipMessage = (issue: PlainHardwareHealthIssue) => {
    const hardwareType = getReadableIssueHardwareType(issue.IssueHardwareType);

    if (issue.HardwareLocationMoved) {
        return `${hardwareType} has been moved from this location.`;
    }

    if (issue.Node && isNodeInactive(issue.Node.NodeStatusCode)) {
        return 'Sensor is inactive.';
    }

    return undefined;
};

export const issueGroupUnboundMessage =
    'The group for this issue was deleted during a system reconfiguration.';

export const getIssueTab = (status: IssueStatusType) => {
    switch (status) {
        case IssueStatusType.Draft:
            return 'draft';
        case IssueStatusType.Closed:
        case IssueStatusType.WontFix:
        case IssueStatusType.NotIssue:
            return 'archived';
        default:
            return 'active';
    }
};

export function buildIssueCommentFilePath(
    formattedIssueNumber: string,
    fileName: string,
) {
    return `/desk/issue/${formattedIssueNumber}/comments/${fileName}`;
}

export const DeskModuleTitle = styled.div`
    font-size: 24px;
    font-weight: 500;
    padding-right: 10px;
`;

const NonAlphaRegex = /[^a-z]/gi;

const findPriorities = buildFindEnumValues(
    IssuePrioritiesByLowerLabel,
    (v) => v.priorityValue,
);
const findSeverities = buildFindEnumValues(
    IssueSeveritiesByLowerLabel,
    (v) => v.severityValue,
);

function buildFindEnumValues<V, R>(
    enumMap: Record<string, V>,
    resultSelector: (value: V) => R,
): (search: string) => R[] {
    return (search: string) => {
        const searchTerms = search.toLowerCase().trim().split(NonAlphaRegex);
        const foundValues = new Set<R>();
        for (const term of searchTerms) {
            const value = enumMap[term];
            if (value) {
                foundValues.add(resultSelector(value));
            }
        }
        return [...foundValues];
    };
}
